/**
 * 打刻タブの「何を出すか・どのボタンを押せるか」だけを決める（R0102 A-4）。
 *
 * ★`src/ui/attendance-anomaly-view.ts` と同じ理由で DOM から切り離す。**判断をここだけに置く**
 *   （描画側＝`src/ui/main.ts` は配列を DOM へ写すだけにする）。
 *
 * ## ★「当日」をクライアント時計で決めない
 *
 * 権限マトリクス §3.6・`.loop/issues/R0102-A4.md`「クライアント時計で営業日を判定しない
 * （サーバの resolve_business_date が正）」。ここでは `events` に含まれる
 * `businessDate`（サーバが焼き付けた値）だけを見て、**もっとも新しい営業日のイベント群**を
 * 「いまの勤務」として扱う。0件・退勤済みなら次は出勤（`CLOCK_IN`）——
 * 押せても実際にはサーバ側（`src/infra/punches.ts` の入口ガード＋
 * `pg_advisory_xact_lock` による同一 staff の直列化。`migrations/0050` で
 * 旧 `migrations/0049` の一意制約は撤去済み）が二重打刻・退勤済み営業日への
 * 再出勤を理由つきで拒否するので、「押せてしまう」側に倒しても安全側
 * （拒否は理由つきで返る。押せない側に倒すと、深夜0時をまたぐ営業日の
 * 切り替わりでボタンが固まる方が実害が大きい）。
 */
import type { PunchType } from "./api.js";

/** ★型そのものは `api.ts` の唯一の契約を使う（ここで別の型を作らない） */
export const PUNCH_TYPES: readonly PunchType[] = ["CLOCK_IN", "BREAK_START", "BREAK_END", "CLOCK_OUT"];

export const PUNCH_LABEL: Readonly<Record<PunchType, string>> = {
  CLOCK_IN: "出勤",
  BREAK_START: "休憩開始",
  BREAK_END: "休憩終了",
  CLOCK_OUT: "退勤",
};

export interface PunchEventLike {
  readonly punchType: PunchType;
  readonly businessDate: string;
}

/** いま押せる打刻の種別（複数のことがある。休憩は必須ではないため） */
export interface PunchButtonState {
  readonly enabled: ReadonlySet<PunchType>;
}

/** `events` のうち、もっとも新しい `businessDate` に属する行だけ（＝いまの勤務） */
export function currentShiftEvents<T extends PunchEventLike>(
  events: readonly T[],
): { businessDate: string | null; events: readonly T[] } {
  if (events.length === 0) return { businessDate: null, events: [] };
  const latest = events.reduce(
    (max, e) => (e.businessDate > max ? e.businessDate : max),
    events[0]!.businessDate,
  );
  return { businessDate: latest, events: events.filter((e) => e.businessDate === latest) };
}

// ---------------------------------------------------------------------
// 打刻の訂正（R0102 A-5）。判断はここ、DOM は main.ts
// ---------------------------------------------------------------------

/** 訂正で選べる種別（現在値そのものはサーバが「値が変わっていません」で拒否するので出さない） */
export function correctionTypeChoices(current: PunchType): readonly PunchType[] {
  return PUNCH_TYPES.filter((t) => t !== current);
}

/**
 * `datetime-local` の値を訂正 API の受理形式（`YYYY-MM-DDTHH:MM:SS`）へ揃える。
 * ★ブラウザは秒が 00 の値を「YYYY-MM-DDTHH:MM」に丸めて返すことがある
 */
export function normalizeOccurredAtInput(value: string): string {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
}

export interface PunchCorrectionLike {
  readonly field: "occurred_at" | "punch_type";
  readonly oldValue: string;
  readonly newValue: string;
  readonly correctedAs: "SELF" | "OTHER";
  readonly correctedAt: string;
  readonly reason: string;
}

const correctionValueLabel = (field: PunchCorrectionLike["field"], value: string): string =>
  field === "punch_type"
    ? (PUNCH_LABEL[value as PunchType] ?? value)
    : value.replace("T", " ");

/**
 * 訂正履歴の1行（§3.6「本人の訂正と manager 以上の訂正は履歴上で区別できること」）。
 * ★`reason` は利用者の自由文。**呼び出し側は必ず textContent で入れること**（XSS）
 */
export function correctionLine(c: PunchCorrectionLike): string {
  const when = c.correctedAt.replace("T", " ").slice(0, 16);
  const what = c.field === "punch_type" ? "種別" : "時刻";
  const who = c.correctedAs === "SELF" ? "本人" : "店長・オーナー";
  return `${when} ${what}: ${correctionValueLabel(c.field, c.oldValue)} → ${correctionValueLabel(c.field, c.newValue)}（${who}）理由: ${c.reason}`;
}

/**
 * 次に押せるボタン（AC-2）。
 * - いまの勤務が無い／退勤済み → 出勤のみ
 * - 開いている休憩がある（休憩開始 > 休憩終了の件数） → 休憩終了のみ
 * - それ以外（出勤済み・退勤前） → 休憩開始・退勤（どちらも選べる。休憩は任意のため）
 */
export function nextPunchOptions(events: readonly PunchEventLike[]): PunchButtonState {
  const shift = currentShiftEvents(events);
  if (shift.events.length === 0) return { enabled: new Set(["CLOCK_IN"]) };
  if (shift.events.some((e) => e.punchType === "CLOCK_OUT")) {
    return { enabled: new Set(["CLOCK_IN"]) };
  }
  const opens = shift.events.filter((e) => e.punchType === "BREAK_START").length;
  const closes = shift.events.filter((e) => e.punchType === "BREAK_END").length;
  if (opens > closes) return { enabled: new Set(["BREAK_END"]) };
  return { enabled: new Set(["BREAK_START", "CLOCK_OUT"]) };
}
